"use client";

import { useCountUp } from "@/lib/hooks/use-count-up";
import SampleDataNote from "@/components/home/mockups/sample-data-note";
import { useTranslation } from "@/lib/hooks/use-translation";

type Tier = {
  name: string;
  /** Legend dot, and the bar segment unless `bar` overrides it. */
  color: string;
  bar?: string;
  /** Members, in thousands — the card prints it with a K. */
  members: number;
  decimals: number;
  share: number;
};

/** Largest first, which is the order the bar reads in. */
const TIERS: Tier[] = [
  {
    name: "Silver",
    color: "#667EEA",
    bar: "linear-gradient(90deg,#8195EC 0%,rgba(102,126,234,0.15) 100%)",
    members: 120.2,
    decimals: 1,
    share: 50,
  },
  { name: "Gold", color: "#A9B6F3", members: 89.2, decimals: 1, share: 30 },
  { name: "Platinum", color: "#D1D8F9", members: 25.2, decimals: 1, share: 15 },
  { name: "Diamond", color: "#EDF0FD", members: 1.23, decimals: 2, share: 5 },
];

/** Bar widths from the comp, which aren't quite the percentages. */
const BAR_WEIGHT = [119.02, 46.07, 32.36, 7.68];
const BAR_TOTAL = BAR_WEIGHT.reduce((a, b) => a + b, 0);

/**
 * TierShareMock — the loyalty-tier breakdown panel that fronts the "รู้ทันธุรกิจ"
 * card in section 4.
 *
 * Built as live UI rather than an exported image so the figures can count up
 * and the bar can grow on arrival. Every measurement is in `em` against a
 * font-size derived from the panel's own width (the `DeviceStage` trick from
 * section 3), so the whole panel scales off one number and stays legible at
 * any card width.
 */
export default function TierShareMock({
  armed = false,
  /**
   * Held after arming, before the first figure moves. The panel is armed when
   * it scrolls into view, but it is still transparent at that point — its own
   * entrance is queued behind the card's — so without this the count is most
   * of the way done by the time anyone can see it.
   */
  lead = 0,
}: {
  armed?: boolean;
  lead?: number;
}) {
  const { t } = useTranslation();

  return (
    <div
      className="aspect-[232/186.5] w-full"
      // Invented member counts and shares — see sample-data-note.tsx.
      data-nosnippet
      style={{ containerType: "inline-size" }}
    >
      <SampleDataNote />
      {/* 1em = the comp's 9.87px title at its 232px panel width. */}
      <div className="flex h-full flex-col justify-between rounded-[0.72em] bg-white p-[0.89em] text-[4.25cqw] shadow-[-0.19em_0.19em_0.85em_rgba(0,0,0,0.05)] outline outline-[0.06em] -outline-offset-[0.06em] outline-[#E6E6E6]">
        <header className="flex items-start justify-between gap-[0.5em]">
          <div className="min-w-0">
            <p className="truncate text-[1em]/[1.56em] font-semibold text-black">
              {t("home.mocks.tiers.shareTitle")}
            </p>
            <p className="truncate text-[0.78em]/[1.11em] font-medium text-black/40">
              {t("home.mocks.tiers.shareSub")}
            </p>
          </div>
          <span className="flex shrink-0 items-center gap-[0.22em] rounded-[0.44em] bg-[#F7F7F7] px-[0.44em] py-[0.22em] text-[0.78em]/[1.11em] font-semibold text-black/40">
            {t("home.mocks.tiers.compare")}
            <span aria-hidden>→</span>
          </span>
        </header>

        <div className="flex flex-col gap-[1.33em]">
          {/* Stacked share bar. Each segment grows from nothing on arrival, on
              the same ease as the numbers beside it. */}
          <div className="flex h-[2.67em] gap-[0.44em] overflow-hidden">
            {TIERS.map((tier, i) => (
              <span
                key={tier.name}
                className="h-full rounded-[0.22em] transition-[width] duration-[1.4s] ease-[cubic-bezier(0.215,0.61,0.355,1)] motion-reduce:transition-none"
                style={{
                  width: armed
                    ? `${(BAR_WEIGHT[i] / BAR_TOTAL) * 100}%`
                    : "0%",
                  background: tier.bar ?? tier.color,
                  transitionDelay: `${lead}ms`,
                }}
              />
            ))}
          </div>

          <ul className="flex flex-col gap-[0.89em]">
            {TIERS.map((tier, i) => (
              <TierRow
                key={tier.name}
                tier={tier}
                armed={armed}
                index={i}
                lead={lead}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function TierRow({
  tier,
  armed,
  index,
  lead,
}: {
  tier: Tier;
  armed: boolean;
  index: number;
  lead: number;
}) {
  // Each row starts a beat after the one above, so the list ticks downward.
  const delay = lead + index * 90;
  const members = useCountUp(tier.members, { armed, delay });
  const share = useCountUp(tier.share, { armed, delay });

  return (
    <li
      className={`flex items-center justify-between gap-[0.44em] pb-[0.33em] text-[0.89em]/[1.33em] ${
        index === TIERS.length - 1 ? "" : "border-b-[0.06em] border-[#E6E6E6]"
      }`}
    >
      <span className="flex min-w-0 flex-1 items-center gap-[0.5em]">
        <span
          aria-hidden
          className="size-[0.93em] shrink-0 rounded-full"
          style={{ background: tier.color }}
        />
        <span className="truncate text-black">{tier.name}</span>
      </span>
      {/* Tabular figures so the numbers don't jitter while they count. */}
      <span className="shrink-0 font-medium text-black tabular-nums">
        {members.toFixed(tier.decimals)}K
      </span>
      <span className="w-[2.5em] shrink-0 text-right text-black/50 tabular-nums">
        {Math.round(share)}%
      </span>
    </li>
  );
}
